// Text with emoji shown as pictures, for the rich text field. Every token
// keeps its exact source text, so an offset in the text is the same
// offset in the rendered field.

import {
  processTextForUnicodeEmoji,
  unicodeEmojiUrls,
} from 'src/services/unicode_emoji/unicode_emoji.js'

const SHORTCODE = /:([\w+-]+):/g

const text = (value) => ({ type: 'text', text: value })

const customTokens = (value, custom) => {
  const out = []
  let last = 0
  SHORTCODE.lastIndex = 0
  let match = SHORTCODE.exec(value)
  while (match) {
    const src = custom.get(match[1])
    if (src) {
      if (match.index > last) out.push(text(value.slice(last, match.index)))
      out.push({ type: 'emoji', text: match[0], src, alt: match[0] })
      last = match.index + match[0].length
    } else {
      // the closing colon of an unknown code can open the next one
      SHORTCODE.lastIndex = match.index + 1
    }
    match = SHORTCODE.exec(value)
  }
  if (last < value.length) out.push(text(value.slice(last)))
  return out
}

// a display name as text and its custom emoji ({ shortcode, url } list);
// unicode: its unicode emoji as Twemoji pictures too
export const nameParts = (name, emoji, { unicode = false } = {}) => {
  const parts = customTokens(
    name,
    new Map((emoji ?? []).map((e) => [e.shortcode, e.url])),
  )
  return unicode
    ? parts.flatMap((part) =>
        part.type === 'text' ? unicodeTokens(part.text) : [part],
      )
    : parts
}

// the first letter of a name for a pill with no picture: custom emoji
// codes and spaces skipped, the handle when the name has no letters
// (Firefox before 125 has no Intl.Segmenter: code points then)
const characters = (text) =>
  Intl.Segmenter
    ? [...new Intl.Segmenter().segment(text)].map(({ segment }) => segment)
    : Array.from(text)

const firstLetter = (text) =>
  characters(text.replace(SHORTCODE, ' ')).find((segment) =>
    /\p{L}|\p{N}/u.test(segment),
  )

export const nameInitial = (name, screenName) =>
  (
    firstLetter(name ?? '') ??
    firstLetter(screenName ?? '') ??
    '?'
  ).toUpperCase()

const unicodeTokens = (value) =>
  processTextForUnicodeEmoji(value, (emoji) => {
    const [src, ...fallbacks] = unicodeEmojiUrls(emoji)
    return { type: 'emoji', text: emoji, src, fallbacks, alt: emoji }
  }).map((part) => (typeof part === 'string' ? text(part) : part))

// Where the server makes a mention: a whitespace-separated word that is
// @name or @name@host, with only ,.;:>?! after it.
const MENTION =
  /(^|\s)@([\w-]+(?:@[\w-]+(?:\.[\w-]+)+)?)(?=[,.;:>?!]*(?:\s|$))/g

// at: where value starts in the whole text; a handle that ends at the
// caret is still being typed and stays text
const mentionTokens = (value, at, mention, caret) => {
  const out = []
  let last = 0
  for (const match of value.matchAll(MENTION)) {
    const start = match.index + match[1].length
    const handle = `@${match[2]}`
    if (at + start + handle.length === caret) continue
    const user = mention(match[2])
    if (!user) continue
    if (start > last) out.push(text(value.slice(last, start)))
    out.push({ type: 'mention', text: handle, ...user })
    last = start + handle.length
  }
  if (last < value.length) out.push(text(value.slice(last)))
  return out
}

// splits the text tokens further; split gets each text and where it starts
const inText = (tokens, split) => {
  let at = 0
  return tokens.flatMap((token) => {
    const start = at
    at += token.text.length
    return token.type === 'text' ? split(token.text, start) : [token]
  })
}

// custom: Map of shortcode -> image URL; unicode: also draw unicode emoji
// as pictures (the "emoji as pictures" setting); mention: handle ->
// { name, avatar } of a known user, or null; caret: the caret's offset
export const emojiTokens = (
  value,
  { custom, unicode = false, mention, caret = -1 },
) => {
  let tokens = customTokens(value, custom)
  if (mention) {
    tokens = inText(tokens, (part, at) =>
      mentionTokens(part, at, mention, caret),
    )
  }
  if (unicode) {
    tokens = inText(tokens, (part) => unicodeTokens(part)).map((token) =>
      // a pill's name follows the setting too
      token.type === 'mention' ? { ...token, unicode } : token,
    )
  }
  return tokens
}

// DOM side. The field holds text nodes, <img data-text> pictures and
// <span data-text> mention pills, and ends with a <br data-end>, which is
// not text itself.
// The browser may add <br> and <div> of its own while editing.

const isBlock = (node) => node.nodeName === 'DIV' || node.nodeName === 'P'
const isEnd = (node) => node.nodeName === 'BR' && 'end' in node.dataset

// A <br> that is the last thing in its line holder only keeps the line
// open, as browsers leave one after deleting; it is not a newline.
const isPlaceholder = (node) => {
  if (isEnd(node)) return true
  let next = node.nextSibling
  while (next?.nodeType === Node.TEXT_NODE && next.nodeValue === '') {
    next = next.nextSibling
  }
  return !next
}

// onFail: what to do when the picture and its fallbacks all fail to load
const emojiImage = (token, { onFail } = {}) => {
  const img = document.createElement('img')
  img.className = 'emoji rich-emoji'
  img.src = token.src
  img.alt = token.alt
  img.title = token.alt
  img.draggable = false
  img.dataset.text = token.text
  const fallbacks = [...(token.fallbacks ?? [])]
  img.onerror = () => {
    if (fallbacks.length) img.src = fallbacks.shift()
    else onFail?.(img)
  }
  return img
}

// a user as one unit: avatar and name, standing for the handle
const mentionPill = (token) => {
  const pill = document.createElement('span')
  pill.className = 'rich-mention'
  pill.contentEditable = 'false'
  pill.dataset.text = token.text
  pill.title = token.text
  const initial = () => {
    const letter = document.createElement('span')
    letter.className = 'rich-mention-avatar -initial'
    letter.textContent = nameInitial(token.name, token.text.slice(1))
    return letter
  }
  let avatar = initial()
  if (token.avatar) {
    avatar = document.createElement('img')
    avatar.className = 'rich-mention-avatar'
    avatar.src = token.avatar
    avatar.alt = ''
    avatar.draggable = false
    avatar.onerror = () => avatar.replaceWith(initial())
  }
  const name = document.createElement('span')
  name.className = 'rich-mention-name'
  // the name's own emoji as pictures; not pieces of text of their own,
  // the pill as a whole stands for the handle
  name.append(
    ...nameParts(token.name, token.emoji, { unicode: token.unicode }).map(
      (part) => {
        if (part.type === 'text') return document.createTextNode(part.text)
        // decoration only: a broken one is left out, as in NameWithEmoji
        const img = emojiImage(part, { onFail: (broken) => broken.remove() })
        delete img.dataset.text
        return img
      },
    ),
  )
  pill.append(avatar, name)
  return pill
}

const nodeFor = (token) => {
  if (token.type === 'emoji') return emojiImage(token)
  if (token.type === 'mention') return mentionPill(token)
  return document.createTextNode(token.text)
}

export const renderTokens = (root, tokens, { end: withEnd = true } = {}) => {
  root.replaceChildren(...tokens.map(nodeFor))
  // always there in the field, so a newline typed at the very end has a
  // line after it and the browser does not add a second one of its own.
  // A copy with more text after it (EmojiInput's caret) goes without.
  if (!withEnd) return
  const end = document.createElement('br')
  end.dataset.end = ''
  root.appendChild(end)
}

// Walks the field in text order, calling visit(node, text) for every
// piece that stands for text: text nodes, pictures, line breaks and the
// newline before a block.
const walk = (root, visit) => {
  let last = ''
  const step = (parent) => {
    for (const node of parent.childNodes) {
      let piece = null
      if (node.nodeType === Node.TEXT_NODE) piece = node.nodeValue
      // a picture or a pill stands for its text as a whole
      else if (node.dataset?.text !== undefined) piece = node.dataset.text
      else if (node.nodeName === 'IMG') piece = node.alt
      else if (node.nodeName === 'BR') piece = isPlaceholder(node) ? null : '\n'
      if (piece !== null) {
        if (visit(node, piece) === false) return false
        if (piece) last = piece
        continue
      }
      if (isBlock(node) && last && !last.endsWith('\n')) {
        if (visit(node, '\n', true) === false) return false
        last = '\n'
      }
      if (node.childNodes?.length && step(node) === false) return false
    }
    return true
  }
  step(root)
}

export const readText = (root) => {
  let out = ''
  walk(root, (_node, piece) => {
    out += piece
  })
  return out
}

// The text offset of a DOM point: the text of everything before it.
export const textOffset = (root, node, offset) => {
  const range = document.createRange()
  range.setStart(root, 0)
  range.setEnd(node, offset)
  const before = document.createElement('div')
  before.appendChild(range.cloneContents())
  return readText(before).length
}

const indexIn = (node) => [...node.parentNode.childNodes].indexOf(node)

// The DOM point of a text offset. An offset inside a picture's text goes
// to after the picture.
export const pointAt = (root, offset) => {
  let remaining = offset
  let point = null
  walk(root, (node, piece, blockNewline) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (remaining <= piece.length) {
        point = { node, offset: remaining }
        return false
      }
    } else if (remaining === 0) {
      point = blockNewline
        ? { node, offset: 0 }
        : { node: node.parentNode, offset: indexIn(node) }
      return false
    } else if (remaining < piece.length) {
      point = { node: node.parentNode, offset: indexIn(node) + 1 }
      return false
    }
    remaining -= piece.length
  })
  if (point) return point
  const end = root.lastChild && isEnd(root.lastChild)
  return { node: root, offset: root.childNodes.length - (end ? 1 : 0) }
}
